import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser, useClerk } from "@clerk/clerk-react";
import TopBar from "../../components/topBar/topBar";
import { apiClient } from "../../api/apiClient";

/* ── Shared icons ─────────────────────────────────────────────────────────── */
const Icons = {
  arrowLeft: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  ),
  chevronDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  userCircle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="3" /><path d="M6.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    </svg>
  ),
  passwordSection: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      <circle cx="12" cy="16" r="1" fill="currentColor" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" />
    </svg>
  ),
  folder: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" />
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  ),
};

/* ── Floating-label input ─────────────────────────────────────────────────── */
const FIELD_ICONS = { user: Icons.user, lock: Icons.lock };

function FloatField({ id, label, type = "text", autoComplete, value, onChange, required, icon }) {
  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        placeholder=" "
        className="peer w-full rounded-3xl border-2 border-mainDark/20 bg-mainWhite pl-12 pr-5 pb-3 pt-7 text-mainDark shadow-result outline-none transition-all duration-200 focus:border-mainBlue focus:shadow-search"
        value={value}
        onChange={onChange}
        required={required}
      />
      {icon && (
        <span className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-mainDark/25 transition-colors duration-200 peer-focus:text-mainBlue peer-[:not(:placeholder-shown)]:text-mainBlue/50">
          {FIELD_ICONS[icon]}
        </span>
      )}
      <label
        htmlFor={id}
        className="pointer-events-none absolute left-12 top-2.5 text-[10px] font-medium uppercase tracking-widest text-mainBlue transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:font-light peer-placeholder-shown:text-mainDark/40 peer-focus:top-2.5 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-widest peer-focus:font-medium peer-focus:text-mainBlue"
      >
        {label}
      </label>
    </div>
  );
}

/* ── Section card ─────────────────────────────────────────────────────────── */
function Section({ icon, title, children, action }) {
  return (
    <section className="mb-6 rounded-result border border-mainDark/30 bg-mainWhite p-6 shadow-result transition-shadow duration-200 hover:shadow-result-hover">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-mainBlue/15 text-mainBlue">
            <span className="h-[18px] w-[18px]">{icon}</span>
          </div>
          <h2 className="text-xl font-normal text-mainDark">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/* ── Save button ──────────────────────────────────────────────────────────── */
function SaveButton({ loading, label, loadingLabel }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-fit rounded-3xl bg-primary px-8 py-3 text-onPrimary shadow-search transition-all duration-200 hover:brightness-110 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.25)] active:translate-y-0 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:hover:shadow-search disabled:hover:brightness-100"
    >
      {loading ? loadingLabel : label}
    </button>
  );
}

/* ── Feedback message ─────────────────────────────────────────────────────── */
function Feedback({ msg, ok }) {
  if (!msg) return null;
  return (
    <p className={`text-sm font-light px-1 ${ok ? "text-mainBlue" : "text-red-500"}`}>
      {msg}
    </p>
  );
}

function UserProfile() {
  const { t, i18n } = useTranslation();
  const { isLoaded, user } = useUser();
  const { signOut } = useClerk();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileMsg, setProfileMsg] = useState("");
  const [profileMsgOk, setProfileMsgOk] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMsg, setPasswordMsg] = useState("");
  const [passwordMsgOk, setPasswordMsgOk] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState("");

  const loadHistory = useCallback(async () => {
    setHistoryError("");
    try {
      const res = await apiClient.get("/search-history");
      setHistory(res.data?.data || []);
    } catch (e) {
      setHistoryError(e.response?.data?.message || t("profile.couldNotLoad"));
    }
  }, [t]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
    }
  }, [user]);

  useEffect(() => {
    if (isLoaded && user) loadHistory();
  }, [isLoaded, user, loadHistory]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg("");
    setProfileSaving(true);
    try {
      await user.update({ firstName: firstName.trim(), lastName: lastName.trim() });
      setProfileMsgOk(true);
      setProfileMsg(t("profile.profileUpdated"));
    } catch (err) {
      setProfileMsgOk(false);
      setProfileMsg(err.errors?.[0]?.message || err.message || t("profile.couldNotProfile"));
    } finally {
      setProfileSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg("");
    if (newPassword !== confirmPassword) {
      setPasswordMsgOk(false);
      setPasswordMsg(t("profile.newPasswordMismatch"));
      return;
    }
    setPasswordSaving(true);
    try {
      await user.updatePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMsgOk(true);
      setPasswordMsg(t("profile.passwordUpdated"));
    } catch (err) {
      setPasswordMsgOk(false);
      setPasswordMsg(err.errors?.[0]?.message || err.message || t("profile.couldNotPassword"));
    } finally {
      setPasswordSaving(false);
    }
  };

  const clearHistory = async () => {
    try {
      await apiClient.delete("/search-history");
      setHistory([]);
    } catch (e) {
      setHistoryError(e.response?.data?.message || t("profile.couldNotClearHistory"));
    }
  };

  if (!isLoaded || !user) return null;

  const primaryEmail =
    user.primaryEmailAddress?.emailAddress ||
    user.emailAddresses?.[0]?.emailAddress ||
    "";

  const displayName = [firstName, lastName].filter(Boolean).join(" ") || primaryEmail;
  const initials = [firstName?.[0], lastName?.[0]].filter(Boolean).join("").toUpperCase() || primaryEmail?.[0]?.toUpperCase() || "?";

  return (
    <div className="min-h-screen">
      <TopBar logo={true} />

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">

        {/* Back to home */}
        <Link
          to="/"
          className="group mb-8 -ml-1 inline-flex items-center gap-2 text-sm font-light text-mainDark/40 transition-colors duration-200 hover:text-mainDark"
        >
          <span className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1">{Icons.arrowLeft}</span>
          {t("login.backHome")}
        </Link>

        {/* ── Hero — full width ── */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-mainBlue/20 text-lg font-medium text-mainDark select-none">
              {initials}
            </div>
            <div>
              <h1 className="text-2xl font-light text-mainDark">{displayName}</h1>
              <p className="text-sm font-light text-mainDark/50">{primaryEmail}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => signOut({ redirectUrl: "/" })}
            className="group flex items-center gap-2 rounded-3xl border border-mainDark/20 px-5 py-2.5 text-sm text-mainDark/60 shadow-search transition-all duration-200 hover:border-mainDark/40 hover:text-mainDark hover:-translate-y-0.5 active:translate-y-0 dark:border-mainAccent/40 dark:text-mainAccent dark:hover:border-mainAccent"
          >
            {t("profile.signOut")}
            <span className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5">{Icons.arrowRight}</span>
          </button>
        </div>

        {/* ── Two-column grid ── */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

          {/* Left — user info + password */}
          <div>
            <Section icon={Icons.userCircle} title={t("profile.account")}>
              <form onSubmit={saveProfile} className="flex flex-col gap-4">
                <FloatField
                  id="firstName" label={t("profile.placeholderFirstName")} icon="user"
                  autoComplete="given-name"
                  value={firstName} onChange={(e) => setFirstName(e.target.value)}
                />
                <FloatField
                  id="lastName" label={t("profile.placeholderLastName")} icon="user"
                  autoComplete="family-name"
                  value={lastName} onChange={(e) => setLastName(e.target.value)}
                />
                <Feedback msg={profileMsg} ok={profileMsgOk} />
                <SaveButton loading={profileSaving} label={t("profile.saveProfile")} loadingLabel={t("profile.saving")} />
              </form>
            </Section>

            <Section icon={Icons.passwordSection} title={t("profile.passwordHeading")}>
              <form onSubmit={savePassword} className="flex flex-col gap-4">
                <FloatField
                  id="currentPassword" label={t("profile.placeholderCurrentPassword")} type="password" icon="lock"
                  autoComplete="current-password"
                  value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <FloatField
                  id="newPassword" label={t("profile.placeholderNewPassword")} type="password" icon="lock"
                  autoComplete="new-password"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                />
                <FloatField
                  id="confirmPassword" label={t("profile.placeholderConfirmPassword")} type="password" icon="lock"
                  autoComplete="new-password"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <Feedback msg={passwordMsg} ok={passwordMsgOk} />
                <SaveButton loading={passwordSaving} label={t("profile.updatePassword")} loadingLabel={t("profile.updating")} />
              </form>
            </Section>
          </div>

          {/* Right — searches + projects */}
          <div>
            <Section
              icon={Icons.clock}
              title={t("profile.recentSearches")}
              action={
                history.length > 0 ? (
                  <button
                    type="button"
                    onClick={clearHistory}
                    className="group flex items-center gap-1.5 text-sm font-light text-mainDark/40 transition-colors duration-200 hover:text-red-500"
                  >
                    {t("profile.clearHistory")}
                    <span className="h-3.5 w-3.5 opacity-0 transition-all duration-200 group-hover:opacity-100">{Icons.trash}</span>
                  </button>
                ) : null
              }
            >
              {historyError && <p className="mb-3 text-sm text-red-500">{historyError}</p>}
              {history.length === 0 ? (
                <p className="text-sm font-light text-mainDark/50">{t("profile.noSearchesYet")}</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((row) => (
                    <li key={row._id} className="group flex items-center gap-3">
                      <span className="h-4 w-4 shrink-0 text-mainDark/20">{Icons.search}</span>
                      <Link
                        to={`/search?query=${encodeURIComponent(row.query)}`}
                        className="flex-1 text-sm text-mainDark underline underline-offset-2 transition-colors duration-200 hover:text-mainBlue"
                      >
                        {row.query}
                      </Link>
                      <span className="text-xs text-mainDark/40">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString(i18n.resolvedLanguage || undefined) : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Link
              to="/projects"
              className="group mb-6 flex items-center justify-between rounded-result border border-mainDark/30 bg-mainWhite p-6 shadow-result transition-all duration-200 hover:shadow-result-hover"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-mainBlue/15 text-mainBlue transition-colors duration-200 group-hover:bg-mainBlue group-hover:text-onPrimary">
                  <span className="h-[18px] w-[18px]">{Icons.folder}</span>
                </div>
                <div>
                  <p className="font-normal text-mainDark">{t("profile.projectsHeading")}</p>
                  <p className="text-sm font-light text-mainDark/50">{t("projects.profileBlurb")}</p>
                </div>
              </div>
              <span className="h-5 w-5 shrink-0 text-mainDark/25 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-mainBlue">
                {Icons.arrowRight}
              </span>
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}

export default UserProfile;
